<template>
  <TopBanner
    layout="full-width"
    currentPage="Forget Password"
    heading="Forget Password"
    bannerClass="banner-auth"
  />

  <section class="py-16 md:py-24">
    <div class="max-w-7xl mx-auto px-6 lg:px-10 xl:px-14">
      <div class="mx-auto w-full max-w-3xl space-y-8">
        <!-- Info / helper -->
        <div
          class="flex items-start gap-4 rounded-2xl bg-amber-50/80 supports-[backdrop-filter]:backdrop-blur-md
                 ring-1 ring-amber-200/70 p-5 md:p-6"
          role="status"
          aria-live="polite"
        >
          <div class="relative shrink-0">
            <div class="h-11 w-11 rounded-full bg-white shadow ring-1 ring-black/5 grid place-items-center">
              <span class="font-icon theme-icon info-icon text-theme-active text-[28px]" aria-hidden="true"></span>
            </div>
          </div>
          <div class="text-amber-900">
            <h4 class="font-semibold text-[16px] md:text-[17px]">Information</h4>
            <p class="mt-1 text-[14px] md:text-[15px] text-amber-800/90">
              Lost your password? Please enter your username or email address. You will receive a link to create a new
              password via email.
            </p>
          </div>
        </div>

        <!-- Card -->
        <div
          class="rounded-2xl bg-white/80 supports-[backdrop-filter]:backdrop-blur-md
                 shadow-xl ring-1 ring-black/5 p-6 md:p-8"
        >
          <form @submit.prevent="handleResetPassword" class="space-y-6">
            <div>
              <label
                for="login-username"
                class="block uppercase text-[11px] tracking-widest text-slate-500"
              >
                Username or email address *
              </label>

              <div
                class="mt-2 flex items-center rounded-xl border bg-white/95 border-slate-200
                       focus-within:ring-2 focus-within:ring-emerald-500/25 focus-within:border-emerald-400 transition"
              >
                <input
                  v-model="email"
                  type="text"
                  id="login-username"
                  autocomplete="username email"
                  class="w-full h-[46px] md:h-[52px] bg-transparent outline-none text-slate-800
                         placeholder-slate-400 px-3 text-[15px] font-medium"
                  placeholder="you@example.com"
                  aria-required="true"
                />
              </div>
            </div>

            <div class="flex items-center gap-3">
              <button
                type="submit"
                :disabled="!email || isLoading"
                class="inline-flex items-center justify-center rounded-full px-6 md:px-8 py-3
                       border-2 border-emerald-600 bg-emerald-600 text-white
                       uppercase tracking-wide text-xs md:text-[12px] font-semibold
                       shadow hover:bg-white hover:text-emerald-700
                       transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <span v-if="!isLoading">Reset password</span>
                <span v-else class="inline-flex items-center gap-2">
                  <svg class="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M12 2a10 10 0 100 20" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                  Processing…
                </span>
              </button>

              <span class="text-slate-500 text-sm">
                We’ll send a reset link if the email/username exists.
              </span>
            </div>
          </form>
        </div>
      </div>
    </div>
  </section>

  <Notification
    :show="notif.show"
    :type="notif.type"
    :message="notif.message"
    :messageDetails="notif.messageDetails"
    @closed="notifClosed"
  />
</template>

<script setup lang="ts">
import { ref } from 'vue'
import TopBanner from '@/components/TopBanner.vue'
import Auth from '@/composables/Auth'
import { handleApiError } from '@/utils/common'
import { NotificationType } from '@/types'
import Notification from '@/widgets/Notification.vue'

const email = ref<string>('')
const isLoading = ref<boolean>(false)
const { forgetPassword } = Auth()
const notif = ref<NotificationType>({ show: false, type: 'success', message: '', messageDetails: ''})

const handleResetPassword = async () => {
  if (email.value !== '' && !isLoading.value) {
    isLoading.value = true
    const response = await forgetPassword(email.value)
    isLoading.value = false

    if (response.status === 200) {
      notif.value = {
        show: true,
        type: 'success',
        message: 'Email Sent',
        messageDetails: 'An email has been sent at your provided address'
      }
      resetForm()
    } else {
      const error = handleApiError(response)
      notif.value = { show: true, type: 'error', message: error, messageDetails: '' }
      setTimeout(() => { notifClosed() }, 4000)
    }
  }
}

const resetForm = (): void => {
  email.value = ''
}

const notifClosed = () => {
  notif.value.show = false
}
</script>

<style scoped>
/* Respect reduced motion for all transitions in this card */
@media (prefers-reduced-motion: reduce) {
  .transition { transition: none !important; }
}
</style>